import React, { useMemo } from 'react';
import type { BrannanWebsiteHeaderSettings } from '@shared/types/WebsiteHeaderSettings.types';
import { BrannanNavigation, EventPageType } from '@graphql/generated';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { InlineEditor } from '@shared/components';
import { Box } from '@withjoy/joykit';
import { PhotoPresentationLayout } from '@shared/utils/photoPresentationLayout';
import { isHeaderLayoutWithJsonContainerId, isHeaderLayoutWithPhotos } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { useTranslation } from '@shared/core';
import { useLayout } from '@shared/context/LayoutProvider';
import { useWebsitePageList } from '@apps/guest/packages/layout-engine/layouts/hooks/useWebsitePageList';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';

const FULL_WIDTH_LAYOUTS: PhotoPresentationLayout[] = ['fullWidth', 'photoSlider', 'photoTrio', 'photoDuoLeft', 'photoDuoRight', 'photoMarquee', 'videoArch'];

export interface HeaderPhotoV2Props {
  websiteHeader: NonNullable<BrannanWebsiteHeaderSettings['websiteHeader']>;
  pageSlug?: string;
  pageType: Maybe<EventPageType>;
  showPlaceholder?: boolean;
  children: React.ReactNode;
}

export const HeaderPhoto = (props: HeaderPhotoV2Props) => {
  const { t } = useTranslation('joykit');
  const tInlineEditor = t('inlineEditor');
  const { children, websiteHeader, pageSlug, pageType, showPlaceholder = false } = props;
  const { brannanNavigation } = useLayout();

  const handleEditPhotoClick = () => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  };

  const handleEditMonogramClick = () => {
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editMonogram' },
    });
  };

  const isMonogram = websiteHeader.headerPhoto?.presentationLayout === 'monogram';
  const isVideoArch = websiteHeader.headerPhoto?.presentationLayout === 'videoArch';

  const elementLabel = useMemo(() => {
    if (isMonogram) {
      return tInlineEditor.type.monogram();
    }
    return tInlineEditor.type.photo();
  }, [isMonogram, tInlineEditor.type]);

  const hasContent = useMemo(() => {
    if (isHeaderLayoutWithPhotos(websiteHeader.headerPhoto)) {
      return websiteHeader.headerPhoto.photos.length > 0;
    }

    if (isHeaderLayoutWithJsonContainerId(websiteHeader.headerPhoto)) {
      return !!websiteHeader.headerPhoto.jsonContainerId;
    }

    return false;
  }, [websiteHeader.headerPhoto]);
  const { pages, loading } = useWebsitePageList();
  const pageIndex = pages?.findIndex(p => p.pageSlug === pageSlug);
  const hasTopPadding = useMemo(() => {
    if (isVideoArch) {
      // For videoArch the padding is handled by the VideoArch component
      return false;
    }
    return hasContent || showPlaceholder;
  }, [hasContent, showPlaceholder, isVideoArch]);

  const { findWelcomePage } = useWelcomePage();
  const { isWelcomePage } = useMemo(() => findWelcomePage(pageSlug, pageType), [findWelcomePage, pageSlug, pageType]);

  if (isWelcomePage || (!loading && pageIndex === 0) || brannanNavigation === BrannanNavigation.singlePage) {
    return (
      <Box paddingTop={hasTopPadding ? { _: 0, sm4: 9 } : 0}>
        <InlineEditor
          elementLabel={elementLabel}
          wrapperType="actionInside"
          actionData={
            isMonogram
              ? {
                  editMonogram: handleEditMonogramClick,
                }
              : {
                  editPhoto: handleEditPhotoClick,
                }
          }
          componentName="headerPhoto"
          pageName={pageType}
          pageSlug={pageSlug || ''}
          isEligibleForInlineEditing={!isVideoArch}
          wrapperCSS={FULL_WIDTH_LAYOUTS.includes(websiteHeader.headerPhoto?.presentationLayout) ? { width: '100vw', left: 'calc(-50vw + 50%)' } : {}}
        >
          {children}
        </InlineEditor>
      </Box>
    );
  }

  return null;
};
